import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtCurrency, fmtNumber, fmtPercent, formatNumberWithSeparator } from "@/lib/format";

const EMPTY = {
  vendor_name: "", target_audience: "", universe_size: "",
  cpp: "", ecpm: "", affinity: 1, in_demo: 1, viewability: 1,
};

export default function VendorParameters() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/vendors");
    setList(data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Fetch defaults to pre-fill new vendor form
  const prefillFromDefaults = async () => {
    try {
      const { data: d } = await api.get("/settings/defaults");
      return {
        ...EMPTY,
        universe_size: d.universe ? String(Math.round(d.universe)) : "",
        cpp: d.tv_cprp ? String(Math.round(d.tv_cprp)) : "",
        ecpm: d.olv_ecpm ? String(Math.round(d.olv_ecpm)) : "",
        in_demo: d.in_demo ?? 1,
        viewability: d.viewability ?? 1,
      };
    } catch {
      return EMPTY;
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        vendor_name: form.vendor_name,
        target_audience: form.target_audience || null,
        universe_size: form.universe_size ? Number(form.universe_size) : null,
        cpp: form.cpp ? Number(form.cpp) : null,
        ecpm: form.ecpm ? Number(form.ecpm) : null,
        affinity: Number(form.affinity || 1),
        in_demo: Number(form.in_demo || 1),
        viewability: Number(form.viewability || 1),
      };
      if (editingId) {
        await api.put(`/vendors/${editingId}`, payload);
        toast.success("Vendor parameters updated");
      } else {
        await api.post("/vendors", payload);
        toast.success("Vendor parameters created");
      }
      setOpen(false); setForm(EMPTY); setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onDelete = async (id) => {
    if (!confirm("Delete vendor parameter?")) return;
    await api.delete(`/vendors/${id}`); toast.success("Deleted"); load();
  };

  const onEdit = (v) => {
    setForm({ ...EMPTY, ...v });
    setEditingId(v.id); setOpen(true);
  };

  const onNew = async () => {
    const seed = await prefillFromDefaults();
    setForm(seed); setEditingId(null); setOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-slate-900">Vendor Parameters</h2>
          <p className="text-sm text-slate-500 mt-1">Reusable calculation templates. One record stores TV CPRP, OLV eCPM, DDF, In-Demo and Viewability for a target audience.</p>
        </div>
        <button onClick={onNew} className="btn-primary" data-testid="new-vendor-btn">
          <Plus className="h-4 w-4" /> Add Vendor Parameters
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard label="Total Templates" value={list.length} accent="deep" />
        <SummaryCard label="With TV CPRP" value={list.filter((v) => (v.cpp || 0) > 0).length} accent="blue" />
        <SummaryCard label="With OLV eCPM" value={list.filter((v) => (v.ecpm || 0) > 0).length} accent="teal" />
      </div>

      <div className="card-harmony overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">
            No vendor parameters yet. Click <b>Add Vendor Parameters</b> to create a reusable template.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left py-3 px-4">Vendor Name</th>
                <th className="text-left py-3 px-4">Target</th>
                <th className="text-right py-3 px-4">Universe</th>
                <th className="text-right py-3 px-4">TV CPRP</th>
                <th className="text-right py-3 px-4">OLV eCPM</th>
                <th className="text-right py-3 px-4">In-Demo</th>
                <th className="text-right py-3 px-4">Viewability</th>
                <th className="text-right py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((v) => (
                <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`vendor-row-${v.id}`}>
                  <td className="py-3 px-4 font-medium text-slate-900">{v.vendor_name}</td>
                  <td className="py-3 px-4 text-slate-600">{v.target_audience || "—"}</td>
                  <td className="py-3 px-4 text-right text-slate-600">{fmtNumber(v.universe_size)}</td>
                  <td className="py-3 px-4 text-right">{v.cpp ? fmtCurrency(v.cpp) : "—"}</td>
                  <td className="py-3 px-4 text-right">{v.ecpm ? fmtCurrency(v.ecpm) : "—"}</td>
                  <td className="py-3 px-4 text-right">{fmtPercent((v.in_demo || 0) * 100)}</td>
                  <td className="py-3 px-4 text-right">{fmtPercent((v.viewability || 0) * 100)}</td>
                  <td className="py-3 px-4 text-right">
                    <button onClick={() => onEdit(v)} className="text-[#0E73C9] mr-3" data-testid={`vendor-edit-${v.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => onDelete(v.id)} className="text-rose-600" data-testid={`vendor-delete-${v.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingId ? "Edit Vendor Parameters" : "New Vendor Parameters"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-3">
            <Field label="Vendor Name *" value={form.vendor_name} onChange={(v) => setForm({ ...form, vendor_name: v })} required colSpan="2" />
            <Field label="Target Audience" value={form.target_audience} onChange={(v) => setForm({ ...form, target_audience: v })} colSpan="2" />
            <Field label="Universe Size" type="number" value={form.universe_size} onChange={(v) => setForm({ ...form, universe_size: v })} format="integer" />
            <Field label="TV CPRP" type="number" value={form.cpp} onChange={(v) => setForm({ ...form, cpp: v })} format="integer" />
            <Field label="OLV eCPM" type="number" value={form.ecpm} onChange={(v) => setForm({ ...form, ecpm: v })} format="integer" />
            <Field label="Affinity" type="number" step="0.01" value={form.affinity} onChange={(v) => setForm({ ...form, affinity: v })} />
            <Field label="In-Demo (0-1)" type="number" step="0.01" value={form.in_demo} onChange={(v) => setForm({ ...form, in_demo: v })} />
            <Field label="Viewability (0-1)" type="number" step="0.01" value={form.viewability} onChange={(v) => setForm({ ...form, viewability: v })} />
            <DialogFooter className="col-span-2">
              <button type="button" onClick={() => setOpen(false)} className="btn-secondary">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="vendor-submit-btn">{editingId ? "Update" : "Create"}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, step, format, colSpan = "1" }) {
  const isInteger = format === "integer";
  const handleChange = (e) => {
    const v = e.target.value;
    if (isInteger) {
      onChange(v.replace(/[^\d]/g, ""));
    } else {
      onChange(v);
    }
  };
  const displayValue = isInteger ? formatNumberWithSeparator(value) : value;
  return (
    <div className={colSpan === "2" ? "col-span-2" : ""}>
      <label className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</label>
      <input
        type={isInteger ? "text" : type}
        inputMode={isInteger ? "numeric" : undefined}
        autoComplete="off"
        step={!isInteger ? step : undefined}
        required={required}
        value={displayValue ?? ""}
        onChange={handleChange}
        className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0E73C9] focus:border-[#0E73C9] outline-none"
      />
    </div>
  );
}

function SummaryCard({ label, value, accent }) {
  const bg = { deep: "bg-[#0A3D91]/8 text-[#0A3D91]", blue: "bg-[#0E73C9]/10 text-[#0E73C9]", teal: "bg-[#1CC7B3]/15 text-[#0B8F80]" };
  return (
    <div className="card-harmony p-5">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="flex items-center justify-between mt-3">
        <div className="font-display text-3xl font-bold text-slate-900">{value}</div>
        <div className={`h-12 w-12 rounded-lg flex items-center justify-center ${bg[accent]} font-display text-xl font-bold`}>
          #
        </div>
      </div>
    </div>
  );
}
